import { FloatingNode, useMergeRefs } from '@floating-ui/react';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef } from 'react';
import { DropdownChild } from "./components/dropdownChild/DropdownChild.js";
import { DropdownContent } from "./components/dropdownContent/DropdownContent.js";
import { DropdownOption } from "./components/dropdownOption/DropdownOption.js";
import { Floating } from "./components/floating/Floating.js";
import { MobileSelect } from "./components/mobileSelect/MobileSelect.js";
import { filterElements } from "./filterElements.js";
import { useDropdownInteractions } from "./hooks/useDropdownInteractions.js";
import { useDropdownState } from "./hooks/useDropdownState.js";
import { useFavorites } from "./hooks/useFavorites.js";
import { useFilterString } from "./hooks/useFilterString.js";
import { useFlip } from "./hooks/useFlip.js";
import { useFloatingDropdown } from "./hooks/useFloatingDropdown.js";
import { useFloatingOffset } from "./hooks/useFloatingOffset.js";
import { useFloatingSize } from "./hooks/useFloatingSize.js";
import { useParsedElements } from "./hooks/useParsedElements.js";
import { useSelectedItemLabel } from "./hooks/useSelectedIndex.js";
import { useSelection } from "./hooks/useSelection.js";
import { useSelectionCandidate } from "./hooks/useSelectionCandidate.js";
import { useSortedElements } from "./hooks/useSortedElements.js";
import { useIsMobile } from "../../helper/hooks/useIsMobile.js";
import { Separator } from "../separator/Separator.js";
import { Spinner } from "../spinner/Spinner.js";
import { Typography } from "../typography/Typography.js";
import styles from "./DropdownWrapper.module.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const DropdownWrapper = /*#__PURE__*/React.forwardRef(({
  children,
  options,
  label,
  defaultValue,
  activeItem,
  onSelection,
  filter = filterElements,
  useChildAsButton = false,
  searchable = false,
  autoSelect = false,
  nullable = false,
  small = false,
  appendTo,
  offset = [0, 0],
  onBlurFn,
  flip = true,
  onFilterStringChange,
  loading = false,
  noItemsHint = 'Keine Elemente gefunden',
  favorites = [],
  width = 'CONTENT',
  horizontalPadding = 0,
  readOnly = false,
  disabled = false,
  ...props
}, forwardedRef) => {
  const isMobile = useIsMobile();
  const listItemsRef = useRef([]);
  const {
    filterString,
    handleFilterStringChange
  } = useFilterString(searchable, activeItem, onFilterStringChange);
  const {
    parsedElements,
    hasCategories
  } = useParsedElements(options, filter, favorites, !!nullable, filterString);
  const {
    reactElements,
    elements
  } = useSortedElements({
    children: options.props.children,
    elements: parsedElements,
    searchString: filterString
  });
  const {
    selectedItemLabel
  } = useSelectedItemLabel(elements, activeItem, filterString);
  const {
    state,
    handleOpenChange,
    handleActiveIndexChange
  } = useDropdownState(disabled);
  const {
    sizeMiddleware,
    calculatedWidth
  } = useFloatingSize({
    width,
    horizontalPadding
  });
  const {
    offsetMiddleware
  } = useFloatingOffset(offset);
  const {
    flipMiddleware
  } = useFlip(flip);
  const {
    nodeId,
    floating
  } = useFloatingDropdown(state.isOpen, handleOpenChange, [flipMiddleware, offsetMiddleware, ...(sizeMiddleware ? [sizeMiddleware] : [])]);
  const referenceRef = useMergeRefs([floating.refs.setReference, forwardedRef]);
  const {
    getItemProps,
    getFloatingProps,
    getReferenceProps
  } = useDropdownInteractions(floating.context, state.activeIndex, listItemsRef, null, handleActiveIndexChange, {
    searchable
  });
  const {
    reactElements: favoriteReactElements,
    elements: favoriteElements
  } = useFavorites({
    children: options.props.children,
    elements: parsedElements,
    favorites
  });
  const {
    handleSelection,
    handleKeyDown
  } = useSelection({
    nullable,
    state,
    handleOpenChange,
    handleFilterStringChange,
    sortedElements: elements,
    favoriteElements,
    elements: parsedElements,
    onSelection
  });
  useSelectionCandidate({
    elements,
    favoriteElements,
    handleActiveIndexChange,
    activeItem,
    filterString
  });
  const handleFilterChanged = useCallback(filterText => {
    handleFilterStringChange(filterText);
    if (!filterText && nullable) {
      // Select the nullable option which comes after the favorites
      handleSelection(favoriteElements.length);
    }
    handleOpenChange(filterText !== undefined);
  }, [favoriteElements.length, handleFilterStringChange, handleOpenChange, handleSelection, nullable]);
  useEffect(() => {
    if (!state.isOpen && searchable) {
      handleFilterStringChange(undefined);
    }
  }, [handleFilterStringChange, searchable, state.isOpen]);
  const getSanitizedIndex = useCallback((index, addFavoriteOffset, addNullableOffset) => {
    const nullableOffset = !filterString && addNullableOffset ? 1 : 0;
    const favoriteOffset = addFavoriteOffset ? favoriteElements.length : 0;
    return index + nullableOffset + favoriteOffset;
  }, [favoriteElements.length, filterString]);
  if (readOnly) {
    return /*#__PURE__*/_jsx(DropdownContent, {
      useChildAsButton: useChildAsButton,
      props: props,
      getReferenceProps: getReferenceProps,
      child: children({
        value: selectedItemLabel
      })
    });
  }
  return /*#__PURE__*/_jsx("div", {
    className: classNames(styles.dropdownWrapper, props.className),
    children: /*#__PURE__*/_jsxs(FloatingNode, {
      id: nodeId,
      children: [/*#__PURE__*/_jsx(DropdownContent, {
        className: classNames({
          [styles.searchable]: searchable
        }),
        useChildAsButton: useChildAsButton,
        ref: referenceRef,
        props: props,
        getReferenceProps: getReferenceProps,
        onKeyDown: handleKeyDown,
        onBlur: onBlurFn,
        child: children({
          handleFilterChange: a => handleFilterChanged(a.target.value),
          value: selectedItemLabel,
          open: state.isOpen,
          ref: referenceRef,
          handleClick: !state.isOpen ? () => handleOpenChange(true) : undefined
        })
      }), isMobile && /*#__PURE__*/_jsx(MobileSelect, {
        parsedElements: parsedElements,
        activeItem: activeItem,
        handleSelection: handleSelection
      }), !isMobile && /*#__PURE__*/_jsxs(Floating, {
        floating: floating,
        isOpen: state.isOpen,
        getFloatingProps: getFloatingProps,
        width: calculatedWidth,
        small: small,
        name: props.name,
        children: [!filterString && favoriteElements.length > 0 && /*#__PURE__*/_jsxs("div", {
          className: styles.favorites,
          children: [/*#__PURE__*/_jsx(Typography, {
            variant: "strong",
            legal: true,
            className: styles.favoriteHeading,
            children: "H\xE4ufig verwendet"
          }), /*#__PURE__*/_jsx("div", {
            className: styles.favoriteOptions,
            children: favoriteReactElements.map((child, index) => {
              const option = favoriteElements[getSanitizedIndex(index)];
              const sanitizedIndex = getSanitizedIndex(index, false, false);
              return option ? /*#__PURE__*/_jsx(DropdownChild, {
                child: child,
                option: option,
                index: sanitizedIndex,
                isActiveIndex: state.activeIndex === sanitizedIndex,
                listItemsRef: listItemsRef,
                onSelection: () => handleSelection(sanitizedIndex),
                activeItem: activeItem,
                getItemProps: getItemProps,
                small: small,
                highlightString: filterString
              }, favoriteElements[sanitizedIndex]?.id ?? index) : undefined;
            })
          }), /*#__PURE__*/_jsx(Separator, {
            marginBottom: "0px",
            marginTop: "5px"
          })]
        }), !filterString && /*#__PURE__*/_jsx(DropdownChild, {
          child: /*#__PURE__*/_jsx(DropdownOption, {
            label: "\u2014"
          }, "UNDEFINED"),
          option: elements[0],
          index: favoriteElements.length,
          isActiveIndex: state.activeIndex === favoriteElements.length,
          listItemsRef: listItemsRef,
          onSelection: () => handleSelection(favoriteElements.length),
          activeItem: activeItem,
          getItemProps: getItemProps,
          small: small,
          visible: !elements[0].filtered
        }), reactElements.map((child, index) => {
          const option = elements[getSanitizedIndex(index, false, true)];
          const sanitizedIndex = getSanitizedIndex(index, true, true);
          return option ? /*#__PURE__*/_jsx(DropdownChild, {
            child: child,
            option: option,
            index: sanitizedIndex,
            isActiveIndex: state.activeIndex === sanitizedIndex,
            listItemsRef: listItemsRef,
            onSelection: () => handleSelection(sanitizedIndex),
            activeItem: activeItem,
            getItemProps: getItemProps,
            small: small,
            highlightString: filterString,
            visible: !option.filtered,
            childOfCategory: hasCategories
          }, option.id) : undefined;
        }), !parsedElements.some(elm => !elm.filtered) && !loading && /*#__PURE__*/_jsx("div", {
          className: styles.noItemsHint,
          children: noItemsHint
        }), loading && /*#__PURE__*/_jsx("div", {
          className: styles.loadingIndicator,
          children: /*#__PURE__*/_jsx(Spinner, {})
        })]
      })]
    })
  });
});